import "react-toastify/dist/ReactToastify.css";
import "./assistant-style.scss";

import { Box, Stack, useTheme } from "@mui/material";
import {
  buildMessageTree,
  getLastAssistantMessage,
  mergeServerDialogue,
} from "src/reusable/Chat/ChatStreaming/services/chatHelpers";
import {
  useActiveDatasetIds,
  useChatStore,
  useSourceDataRecordIds,
} from "./stores/ChatStore";

import AAAssistantText from "./components/AAAssistantText";
import { useProvenanceTurnRefresh } from "./services/ProvenanceService";
import AAChatInputBar from "./components/ChatInput/AAChatInputBar";
import AAChatInputButtons from "src/views/assistant/components/ChatInput/AAChatInputButtons";
import AASidebar from "./components/ResponsiveSidebar/AASidebar";
import InChatDocumentViewer from "./components/sources/InChatDocumentViewerLazy";
import AtomAssistChatInput from "./components/AtomAssistChatInput";
import AtomAssistHeader from "./components/header/AtomAssistHeader";
import AtomAssistTypingIndicator from "../../reusable/Chat/ChatMessage/AtomAssistTypingIndicator";
import { Can } from "src/helpers/Auth";
import ChatContainer from "../../reusable/Chat/ChatContainer";
import { ChatStreamProvider } from "src/reusable/Chat/ChatStreaming/context/ChatStreamContext";
import { ChatWrapperContainer } from "src/reusable/Chat/StyledContainers/StyledContainers";
import WorkflowView from "../products/Products";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { useAtomAssistChat } from "./hooks/useAtomAssistChat";
import { useEffect, useMemo, useRef } from "react";
import { useOnboardingStore } from "./stores/OnboardingStore";
import { useFfnRecordStore } from "./stores/FfnRecordStore";
import { useHistory } from "react-router-dom";
import useLoadDialogue from "./hooks/useLoadDialogue";
import useStartChatOnMount from "src/reusable/Chat/hooks/useStartChatOnMount";
import { ContentType, ImageContent } from "./types";
import useDefaultModelCapabilities from "./hooks/useDefaultModelCapabilities";
import { useChatKeyboardShortcuts } from "./hooks/useChatKeyboardShortcuts";
import { useGetUserProfile } from "src/views/users/ProfileService";
import useConversationTabStatus from "src/components/useConversationTabStatus";
import useConversationStatusInputs from "./hooks/useConversationStatusInputs";

const AtomAssistPage = ({
  dialogueId,
  initialUserMessage,
  initialImages,
  initialSendId,
  composerPlaceholder,
}: {
  dialogueId: number;
  initialUserMessage?: string;
  /** VLM images to include with initial message (from splash screen) */
  initialImages?: ImageContent[];
  /** send_id captured with the splash-screen ai_message_sent (#29704) */
  initialSendId?: string;
  /** Project-level composer placeholder (spec §5.10); default copy when omitted. */
  composerPlaceholder?: string;
}) => {
  const history = useHistory();

  // ✅ Use new ChatStore - single source of truth
  const {
    config: chatConfig,
    responseMode,
    lockResponseMode,
    setActiveDialogueId,
    setDropdownDatasets,
    setDialogue: setStoreDialogue,
    setInitialUserMessage,
    setInitialSendId,
  } = useChatStore();

  // ✅ Get active dataset IDs from store's computed selector
  const activeDatasetIds = useActiveDatasetIds();

  // ✅ Get source data record IDs for document-scoped search
  const sourceDataRecordIds = useSourceDataRecordIds();

  // ✅ Load default model capabilities (GeneralLLM) for vision support detection
  useDefaultModelCapabilities();

  // Global keyboard shortcuts (Ctrl+L new chat, Shift+Esc focus input)
  useChatKeyboardShortcuts();

  // ✅ Update dropdown datasets from config
  useEffect(() => {
    if (chatConfig?.datasets) {
      setDropdownDatasets(chatConfig.datasets);
    }
  }, [chatConfig?.datasets, setDropdownDatasets, dialogueId]);

  // ✅ Update active dialogue ID in store
  useEffect(() => {
    setActiveDialogueId(dialogueId);
    // Drop any 50.59 record panel state carried over from another dialogue;
    // this dialogue's own record (if any) re-populates from its tool
    // responses on load.
    useFfnRecordStore.getState().clearForDialogue(dialogueId);
  }, [dialogueId, setActiveDialogueId]);

  // ✅ Don't use ref for initialUserMessage - it needs to update from splash screen state
  // dialogueId is threaded as a prop so the embedded-in-projects mount
  // (no :dialogueId URL segment) still loads dialogue history + config.
  const { dialogue: loadedDialogue } = useLoadDialogue({
    initialUserMessage,
    dialogueId,
    restoreDocumentScope: true,
  });

  const theme = useTheme();

  // ✅ Build datasets for streaming from store's computed selector
  const datasetsToUse = activeDatasetIds.map((id) => ({ id }));

  const chatConfigForStreaming = {
    dialogueId: dialogueId,
    modelId: chatConfig?.modelId,
    modelVersionNum: chatConfig?.modelVersionNum,
    datasetsForSearchContext: datasetsToUse,
    sourceDataRecordIds:
      sourceDataRecordIds.length > 0 ? sourceDataRecordIds : undefined,
    numSearchResults: chatConfig?.topSearchResults,
    textGenModelParams: chatConfig?.chatControlMetadata,
    systemPrompt: chatConfig?.systemPrompt,
    toolCalling: true,
    chatConfigId: chatConfig?.chatConfigId,
    product: "atom-assist",
    responseMode,
  };

  const dialogueTree = useMemo(() => {
    if (
      !loadedDialogue ||
      !loadedDialogue.messages ||
      loadedDialogue.messages.length === 0
    ) {
      return [];
    }
    const tree = buildMessageTree(loadedDialogue.messages);
    // Restore per-message routing metadata from the persisted dialogue config
    // so the in-chat "Using X" label reappears on page refresh.
    // Use immutable mapping to avoid corrupting react-query cached data.
    const slug = loadedDialogue.config?.routing_persona_slug;
    if (slug) {
      const routingSnapshot = {
        persona_slug: slug,
        persona_name: loadedDialogue.persona?.name || slug,
        confidence: 0,
        reasoning: "Restored from conversation history",
      };
      return tree.map((msg) =>
        msg.type === "Assistant" && !msg.routing
          ? { ...msg, routing: routingSnapshot }
          : msg,
      );
    }
    return tree;
  }, [loadedDialogue?.messages]);

  const {
    dialogue,
    isConnected: isChatConnected,
    isConnectionError: isChatConnectionError,
    streamError: chatStreamError,
    errorMetadata,
    isEnded: isChatEnded,
    isLoading: isChatLoading,
    isMessageStreamStopped: isChatMessageStreamStopped,
    isTurnActive,
    responseSources,
    setResponseSources,
    terminateConnection,
    // Raw turn starters: the page hands out the wrapped copies from
    // useConversationStatusInputs so a user-started turn is recorded.
    emptySubmission: rawEmptySubmission,
    sendUserMessage: rawSendUserMessage,
    resendUserMessage: rawResendUserMessage,
    regenerateResponse: rawRegenerateResponse,
    setAsPreferredMessage,
    setDialogue,
    reconstructArtifactsFromMessages,
    tokenUsage,
    setTokenUsage,
    connectToStream,
    retryConnection,
    lastDialogueId,
    // The dialogue the live store is bound to. On a switch it still names the
    // previous chat for one render (useOwnChatStream adopts during render), so
    // the status hook needs it to know whose messages it is looking at.
    configDialogueId,
    activeSubAgents,
    pendingMessageQueue,
    queueMessage,
    removeQueuedMessage,
    store: chatStore,
  } = useAtomAssistChat(chatConfigForStreaming, dialogueTree);

  // Any message in the conversation (just submitted, or loaded from history)
  // fixes the response mode; the splash resets it for the next conversation.
  useEffect(() => {
    if (dialogue.length > 0) {
      lockResponseMode();
    }
  }, [dialogue.length, lockResponseMode]);

  // Show the typing indicator until we've actually received thinking or text
  // tokens from the model, not just until an Assistant message shell exists.
  const lastMsg = dialogue[dialogue.length - 1];
  const isAssistantResponding = lastMsg?.type === "Assistant";
  const assistantHasModelContent =
    isAssistantResponding &&
    lastMsg.contentParts?.some(
      (p) =>
        (p.type === ContentType.THINK || p.type === ContentType.TEXT) && p.text,
    );
  const showTypingIndicator =
    (isChatLoading ||
      (isTurnActive && isAssistantResponding && !assistantHasModelContent)) &&
    !assistantHasModelContent;
  // A sub-agent still running means the assistant is doing background work the
  // user should wait on — surface that explicitly in the loading indicator.
  const hasActiveSubAgent =
    activeSubAgents?.some(
      (sa) => sa.status !== "completed" && sa.status !== "error",
    ) ?? false;

  // Onboarding emits pre-rendered template messages instantly — no LLM
  // inference to wait on — so the generic "thinking" indicator is misleading.
  // Derive from the dialogue's persona slug (source of truth) rather than
  // a separate store flag so it survives page reloads.
  const isOnboardingSession = useChatStore(
    (s) => s.persona?.slug === "ftux-onboarding",
  );

  // The persona slug stays attached to a FTUX dialogue forever, even after
  // onboarding_completed flips to true. So a sidebar click that lands on
  // the user's old completed FTUX dialogue would otherwise re-mirror
  // isOnboarding=true purely from the slug. Gate on the user profile so
  // we only treat the dialogue as live FTUX when the user actually still
  // needs onboarding.
  const { data: userProfile } = useGetUserProfile();
  const userOnboardingCompleted = userProfile?.onboarding_completed === true;
  const isLiveOnboarding = isOnboardingSession && !userOnboardingCompleted;

  // Mirror the derived onboarding state into the store flag that the sidebar,
  // header progress bar, and telemetry handlers read. Without this, Verification
  // tab / OnboardingProgress / verification_opened + citation_clicked events
  // never trigger because isOnboarding stays false.
  //
  // Guard against re-asserting "true" after ``onboarding_complete`` has
  // already called resetOnboarding — without this gate the ChatStore
  // persona slug lags (still ftux-onboarding) so the next render flips
  // the store flag back on and clobbers the reset. The event handler
  // records the completed dialogue id on ``finalizedDialogueId`` before
  // resetting, which lets us short-circuit for exactly that dialogue.
  const setIsOnboarding = useOnboardingStore((s) => s.setIsOnboarding);
  const setOnboardingDialogueId = useOnboardingStore(
    (s) => s.setOnboardingDialogueId,
  );
  const finalizedDialogueId = useOnboardingStore((s) => s.finalizedDialogueId);
  useEffect(() => {
    if (
      isLiveOnboarding &&
      dialogueId != null &&
      dialogueId === finalizedDialogueId
    ) {
      // This dialogue just emitted onboarding_complete — leave the reset
      // in place, don't mirror the stale ftux-onboarding slug back in.
      return;
    }
    setIsOnboarding(isLiveOnboarding);
    setOnboardingDialogueId(isLiveOnboarding ? dialogueId : null);
  }, [
    isLiveOnboarding,
    dialogueId,
    finalizedDialogueId,
    setIsOnboarding,
    setOnboardingDialogueId,
  ]);

  const LoadingIndicator = useMemo(
    () => () =>
      isOnboardingSession ? null : (
        <AtomAssistTypingIndicator
          showTimer
          backgroundWork={hasActiveSubAgent}
        />
      ),
    [isOnboardingSession, hasActiveSubAgent],
  );

  // Set when probe() joins via legacy replay (wipe + DB replay) for this id.
  const replayJoinedRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    // The block only outlives the replay it protects on embedded mounts,
    // where a dialogue switch doesn't remount the page.
    replayJoinedRef.current = undefined;
  }, [dialogueId]);
  // Cold-load resume point from the snapshot; ready once the snapshot loaded.
  const snapshotRef = useRef<{ cursor?: string | null; ready: boolean }>({
    ready: false,
  });
  snapshotRef.current = {
    cursor: loadedDialogue?.stream_cursor,
    ready: Boolean(loadedDialogue),
  };

  // The dialogue a server snapshot was last applied for.
  const hydratedDialogueRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // After a replay join wiped state, replay owns the rebuild (dup-user bug).
    if (replayJoinedRef.current === dialogueId) return;
    // Live session state beats the REST snapshot — mid-run hydration would
    // clobber streamed parts (known gap until durable-SSE: see PR 2133 notes).
    if (
      lastDialogueId === dialogueId &&
      (dialogue.length > 0 || isChatConnected)
    )
      return;
    // A seeded dialogue's first turn streams before lastDialogueId is set.
    if (hydratedDialogueRef.current === dialogueId && isChatConnected) return;
    if (dialogueTree.length > 0) {
      // A refetch of the dialogue on screen keeps what the server has not saved yet.
      const again = hydratedDialogueRef.current === dialogueId;
      hydratedDialogueRef.current = dialogueId;
      setDialogue(
        again
          ? (current) => mergeServerDialogue(dialogueTree, current)
          : dialogueTree,
      );
      setStoreDialogue(dialogueTree);
      reconstructArtifactsFromMessages(dialogueTree);
    }
  }, [
    dialogueTree,
    dialogueId,
    lastDialogueId,
    dialogue.length,
    isChatConnected,
    setDialogue,
    setStoreDialogue,
    reconstructArtifactsFromMessages,
  ]);

  // Seed token usage from loaded dialogue config (for context window gauge on history load)
  useEffect(() => {
    if (loadedDialogue?.config?.token_usage) {
      setTokenUsage(loadedDialogue.config.token_usage);
    }
  }, [loadedDialogue?.config?.token_usage, dialogueId, setTokenUsage]);

  const lastAssistantMessage = getLastAssistantMessage(dialogue);

  // Where this dialogue's persisted history ends. useConversationStatusInputs
  // takes this as its baseline when the live store has nothing for this chat
  // yet, and treats an Assistant message after it as a turn that ran here.
  const historyLastAssistantId = useMemo(
    () => getLastAssistantMessage(dialogueTree)?.id ?? null,
    [dialogueTree],
  );

  // The whole live Assistant chain, not just its last id: a replay or
  // `retryConnection` rebuild streams the persisted messages back over several
  // frames, so mid-rebuild the last id is an older one.
  const liveAssistantIds = useMemo(
    () =>
      dialogue
        .filter((m) => m.type === "Assistant" && m.id)
        .map((m) => String(m.id)),
    [dialogue],
  );

  // The tab disc and the header chip treat "complete" as an *unseen response*
  // marker, so it only counts a turn that ran here, in this chat. Both read the
  // one status derived here — including the onboarding guard — so they stay in
  // step.
  const { conversationStatus, turnActions } = useConversationStatusInputs({
    dialogueId,
    hasConversation: dialogue.length > 0,
    isComplete: isChatEnded || isAssistantResponding,
    isError: isChatConnectionError || Boolean(chatStreamError),
    isOnboarding: isLiveOnboarding,
    isWorking: isTurnActive || isChatLoading,
    historyLoaded: Number(loadedDialogue?.id) === dialogueId,
    historyLastAssistantId,
    liveDialogueId: configDialogueId ?? null,
    liveAssistantIds,
    sendUserMessage: rawSendUserMessage,
    resendUserMessage: rawResendUserMessage,
    regenerateResponse: rawRegenerateResponse,
    emptySubmission: rawEmptySubmission,
  });
  const {
    sendUserMessage,
    resendUserMessage,
    regenerateResponse,
    emptySubmission,
  } = turnActions;
  // The status the tab is actually showing: an acknowledged "complete" reads
  // back as "ready", and the chip follows the disc rather than the raw status.
  const shownConversationStatus = useConversationTabStatus(conversationStatus);

  // Refresh the provenance funnels once the TURN settles. Keyed off
  // isTurnActive/isLoading, NOT isEnded: in a live agent session the per-turn
  // "end" event never sets isEnded (that's a session-terminal signal), so an
  // isEnded-keyed refresh never fired and the Sources panel kept the stale
  // pre-turn payload until a manual reload.
  useProvenanceTurnRefresh(
    !isTurnActive && !isChatLoading,
    dialogueId,
    lastAssistantMessage?.id,
  );

  // Use the original useStartChatOnMount
  // Pass clearInitialMessage to clear the message after sending (prevents duplicate on refresh)
  useStartChatOnMount({
    initialUserMessage,
    initialImages,
    initialSendId,
    clearInitialMessage: () => {
      setInitialUserMessage(undefined);
      setInitialSendId(undefined);
    },
    emptySubmission,
    sendUserMessage,
  });

  // Reconnect to an active agent session on page load (e.g., after refresh).
  // Only runs once per dialogueId — if no session is active, it's a no-op.
  // Uses refs to avoid dependency churn during streaming (isChatConnected/isChatLoading
  // change on every SSE event and would cause excessive effect evaluations).
  const connectToStreamRef = useRef(connectToStream);
  connectToStreamRef.current = connectToStream;
  const streamingStateRef = useRef({
    isChatConnected,
    isChatLoading,
    isChatMessageStreamStopped,
  });
  streamingStateRef.current = {
    isChatConnected,
    isChatLoading,
    isChatMessageStreamStopped,
  };

  useEffect(() => {
    const MAX_RETRIES = 2;
    if (!dialogueId || initialUserMessage) return;

    let cancelled = false;
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const probe = (attempt: number) => {
      if (cancelled || attempt >= MAX_RETRIES) return;

      const {
        isChatConnected: connected,
        isChatLoading: loading,
        isChatMessageStreamStopped: stopped,
      } = streamingStateRef.current;
      if (connected || loading || stopped) return;

      axios
        .get(`${nuclearn_api_url}/chat/session-active/${dialogueId}`)
        .then((resp) => {
          if (cancelled) return;
          if (resp.data?.active) {
            const { cursor, ready } = snapshotRef.current;
            if (ready && cursor) {
              // Snapshot is hydrated; resume the stream from its cursor.
              connectToStreamRef.current(dialogueId, { cursor });
            } else {
              // No cursor (old backend / snapshot pending) — legacy replay.
              replayJoinedRef.current = dialogueId;
              connectToStreamRef.current(dialogueId, { replay: true });
            }
          } else {
            // Session not active yet — retry after delay (may be initializing)
            timeouts.push(setTimeout(() => probe(attempt + 1), 2000));
          }
        })
        .catch(() => {
          if (cancelled) return;
          // Transient failure — retry after delay
          timeouts.push(setTimeout(() => probe(attempt + 1), 2000));
        });
    };

    // Delay to let useStartChatOnMount fire first (it has a 100ms delay)
    timeouts.push(setTimeout(() => probe(0), 200));

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, [dialogueId, initialUserMessage]);

  const onNewChat = () => {
    // Clear uploaded files before navigating to splash
    // This prevents failed/corrupt files from persisting to the new chat
    useChatStore.getState().clearUploadedFiles();

    // Navigate to splash page
    history.push("/assistant");
  };

  return (
    <Can
      perform="model:read"
      product="atom-assist"
      yes={() => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            // Bound the page to the viewport (minus app header + footer) so the
            // chat scroller and sources sidebar scroll internally instead of the
            // whole document growing with content. Matches AtomAssistPageLoad and
            // the sibling chat pages (PartsAIChat, VnvAssistChat, etc.); the
            // CoreUI shell is only min-height:100vh, so height:100% would grow.
            height: "calc(100vh - 97px)",
            overflow: "hidden",
          }}
        >
          <Box
            id="atom-assist-header"
            sx={{
              flexShrink: 0,
              py: 1,
              bgcolor: "background.paper",
              width: "100%",
              display: "flex",
              flexDirection: "row",
              borderBottom: `1px solid ${theme.palette.divider}`,
              alignItems: "center",
            }}
          >
            <AtomAssistHeader
              dialogueId={dialogueId}
              onNewChat={onNewChat}
              dialogue={dialogue}
              conversationStatus={shownConversationStatus}
            />
          </Box>
          <ChatStreamProvider store={chatStore}>
            <Stack
              direction="row"
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <ChatWrapperContainer>
                <ChatContainer
                  dialogueId={dialogueId}
                  dialogue={dialogue}
                  isChatConnectionError={isChatConnectionError}
                  chatStreamError={chatStreamError}
                  errorMetadata={errorMetadata}
                  isChatConnected={isChatConnected}
                  isChatEnded={isChatEnded}
                  isChatLoading={showTypingIndicator}
                  isTurnActive={isTurnActive}
                  isChatMessageStreamStopped={isChatMessageStreamStopped}
                  startNewChat={onNewChat}
                  resendUserMessage={resendUserMessage}
                  regenerateResponse={regenerateResponse}
                  setAsPreferredMessage={setAsPreferredMessage}
                  onRetryConnection={
                    lastDialogueId ? retryConnection : undefined
                  }
                  CustomLoadingIndicator={LoadingIndicator}
                  ChatPromptInput={
                    <AtomAssistChatInput
                      dialogue={dialogue}
                      dialogueId={dialogueId}
                      isChatConnected={isChatConnected}
                      isChatLoading={isChatLoading}
                      isChatEnded={isChatEnded}
                      isTurnActive={isTurnActive}
                      terminateConnection={terminateConnection}
                      sendUserMessage={sendUserMessage}
                      InputButtons={AAChatInputButtons}
                      InputBar={AAChatInputBar}
                      tokenUsage={tokenUsage}
                      pendingMessageQueue={pendingMessageQueue}
                      queueMessage={queueMessage}
                      removeQueuedMessage={removeQueuedMessage}
                      composerPlaceholder={composerPlaceholder}
                    />
                  }
                  AssistantTextMetadataProps={{ lastAssistantMessage }}
                  CustomAssistantText={AAAssistantText}
                  CustomAssistantTextProps={{
                    isChatConnected: isChatConnected,
                    responseSources,
                    onSourcesUpdate: setResponseSources,
                  }}
                  MoreAssistantActionsProps={{
                    dialogue,
                    lastAssistantMessage,
                  }}
                  activeSubAgents={activeSubAgents}
                />
              </ChatWrapperContainer>
              <AASidebar
                dialogueId={dialogueId}
                responseSources={responseSources}
                lastAssistantMessage={lastAssistantMessage}
              />
            </Stack>
          </ChatStreamProvider>
          {/* In-context annotated source viewer (#20423): a right-anchored
              modal/overlay above the right (Sources) side, opened from the
              Sources tab for dataset-backed documents — a focused, document-only
              reader with the cited/read highlight overlay. Projects use
              openFileInViewer instead, so this is only on the /assistant page. */}
          <InChatDocumentViewer />
        </Box>
      )}
      no={() => <WorkflowView />}
    />
  );
};

export default AtomAssistPage;
